import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { message } from '../utils/antdMessage';
import {
  emptyMemberNote,
  getMemberRealName,
  memberNoteKey
} from '../utils/dashboard/commanderMemberNoteHelpers';
import { digitsOnly, maskPhoneDigits, phoneHasMask } from '../utils/dashboard/phoneMask';

/**
 * 指挥员私有：备注名 / 电话 / 描述
 * focusSection: 'remark' | 'phone' | 'description'
 */
export default function MemberNoteEditorModal({
  open,
  member,
  note,
  focusSection = 'remark',
  onClose,
  onSave
}) {
  const key = memberNoteKey(member);
  const realName = getMemberRealName(member);
  const [remarkName, setRemarkName] = useState('');
  const [phones, setPhones] = useState([]);
  const [description, setDescription] = useState('');
  const [draftPhone, setDraftPhone] = useState('');
  const [addingPhone, setAddingPhone] = useState(false);
  const [revealed, setRevealed] = useState({});
  const [saving, setSaving] = useState(false);

  // 只在「打开弹窗 / 换队员」时灌入初始值。
  // 不可依赖 note 引用：父级列表轮询会反复 new emptyMemberNote() / 换 notes map，
  // 若写入依赖会导致打字中途被清空。
  const seededKeyRef = useRef(null);
  useEffect(() => {
    if (!open) {
      seededKeyRef.current = null;
      return;
    }
    if (seededKeyRef.current === key) return;
    seededKeyRef.current = key;
    const src = note || emptyMemberNote(key);
    setRemarkName(String(src.remark_name || ''));
    setPhones(Array.isArray(src.phones) ? src.phones.map(digitsOnly).filter(Boolean) : []);
    setDescription(String(src.description || ''));
    setDraftPhone('');
    setAddingPhone(false);
    setRevealed({});
    setSaving(false);
  }, [open, key, note]);

  const title = useMemo(() => {
    if (focusSection === 'phone') return '电话';
    if (focusSection === 'description') return '描述';
    return '备注';
  }, [focusSection]);

  if (!open || !member || typeof document === 'undefined') return null;

  const commit = async ({ clearAll = false } = {}) => {
    if (!key) {
      message.error('缺少队员标识，无法保存');
      return;
    }
    if (typeof onSave !== 'function') {
      message.error('保存接口未接线');
      return;
    }
    setSaving(true);
    try {
      const payload = clearAll
        ? { remark_name: '', phones: [], description: '' }
        : {
          remark_name: remarkName,
          phones,
          description
        };
      await onSave(key, payload);
      message.success(clearAll ? '已清除备注信息' : '已保存');
      onClose?.();
    } catch (err) {
      const msg = String(err?.message || '保存失败');
      message.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const addPhone = () => {
    const d = digitsOnly(draftPhone);
    if (!d) {
      message.warning('请输入电话号码');
      return;
    }
    if (![6, 11].includes(d.length) && d.length < 6) {
      message.warning('请输入 11 位手机号或 6 位虚拟网号');
      return;
    }
    if (phones.includes(d)) {
      message.info('该号码已添加');
      return;
    }
    setPhones((prev) => [...prev, d]);
    setDraftPhone('');
    setAddingPhone(false);
  };

  const panel = (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10060,
        background: 'rgba(15,23,42,0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        style={{
          width: 'min(420px, 100%)',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 16px 40px rgba(15,23,42,0.22)',
          border: '1px solid #e2e8f0',
          overflow: 'hidden'
        }}
      >
        <div
          style={{
            padding: '12px 16px',
            borderBottom: '1px solid #eef2f7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>{title}</div>
          <button
            type="button"
            onClick={() => onClose?.()}
            style={{
              border: 'none',
              background: 'transparent',
              color: '#94a3b8',
              cursor: 'pointer',
              fontSize: 18,
              lineHeight: 1
            }}
            aria-label="关闭"
          >
            ×
          </button>
        </div>

        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: 12, color: '#64748b' }}>
            队员真实姓名：
            <span style={{ color: '#0f172a', fontWeight: 600 }}>{realName || '—'}</span>
          </div>

          <div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 6 }}>备注名</div>
            <input
              autoFocus={focusSection === 'remark'}
              value={remarkName}
              onChange={(e) => setRemarkName(e.target.value)}
              placeholder="输入备注名后，列表将显示此名称"
              maxLength={120}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                border: '1px solid #e2e8f0',
                borderRadius: 10,
                padding: '10px 12px',
                fontSize: 14,
                outline: 'none'
              }}
            />
          </div>

          <div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 6 }}>电话</div>
            <div
              style={{
                border: '1px solid #e2e8f0',
                borderRadius: 10,
                padding: 10,
                minHeight: 44
              }}
            >
              {phones.map((p) => {
                const show = !!revealed[p];
                const text = maskPhoneDigits(p, show);
                return (
                  <div
                    key={p}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 8,
                      marginBottom: 8
                    }}
                  >
                    <button
                      type="button"
                      title={phoneHasMask(p) ? (show ? '已显示全部数字' : '点击 * 显示全部数字') : ''}
                      onClick={() => {
                        if (!phoneHasMask(p)) return;
                        setRevealed((prev) => ({ ...prev, [p]: !prev[p] }));
                      }}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        padding: 0,
                        cursor: phoneHasMask(p) ? 'pointer' : 'default',
                        fontSize: 14,
                        color: '#0f172a',
                        letterSpacing: 0.5,
                        fontFamily: 'ui-monospace, Menlo, Consolas, monospace'
                      }}
                    >
                      {text}
                    </button>
                    <button
                      type="button"
                      onClick={() => setPhones((prev) => prev.filter((x) => x !== p))}
                      style={{
                        border: 'none',
                        background: 'transparent',
                        color: '#94a3b8',
                        cursor: 'pointer',
                        fontSize: 12
                      }}
                    >
                      删除
                    </button>
                  </div>
                );
              })}

              {addingPhone ? (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    autoFocus
                    value={draftPhone}
                    onChange={(e) => setDraftPhone(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addPhone();
                      }
                    }}
                    placeholder="11位手机或6位虚拟网"
                    style={{
                      flex: 1,
                      border: '1px solid #cbd5e1',
                      borderRadius: 8,
                      padding: '8px 10px',
                      fontSize: 13
                    }}
                  />
                  <button
                    type="button"
                    onClick={addPhone}
                    style={{
                      border: 'none',
                      background: '#2563eb',
                      color: '#fff',
                      borderRadius: 8,
                      padding: '8px 12px',
                      cursor: 'pointer',
                      fontSize: 13
                    }}
                  >
                    确定
                  </button>
                  <button
                    type="button"
                    onClick={() => { setAddingPhone(false); setDraftPhone(''); }}
                    style={{
                      border: '1px solid #e2e8f0',
                      background: '#fff',
                      color: '#64748b',
                      borderRadius: 8,
                      padding: '8px 10px',
                      cursor: 'pointer',
                      fontSize: 13
                    }}
                  >
                    取消
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setAddingPhone(true)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    border: 'none',
                    background: 'transparent',
                    color: '#2563eb',
                    cursor: 'pointer',
                    padding: 0,
                    fontSize: 14
                  }}
                >
                  <span
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      border: '1.5px solid #2563eb',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 16,
                      lineHeight: 1,
                      fontWeight: 600
                    }}
                  >
                    +
                  </span>
                  添加电话
                </button>
              )}
            </div>
          </div>

          <div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 6 }}>描述</div>
            <textarea
              autoFocus={focusSection === 'description'}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="添加更多备注信息"
              rows={4}
              maxLength={2000}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                border: '1px solid #e2e8f0',
                borderRadius: 10,
                padding: '10px 12px',
                fontSize: 14,
                outline: 'none',
                resize: 'vertical',
                minHeight: 88
              }}
            />
          </div>
        </div>

        <div
          style={{
            padding: '10px 16px 14px',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 8,
            borderTop: '1px solid #eef2f7'
          }}
        >
          <button
            type="button"
            disabled={saving}
            onClick={() => commit({ clearAll: true })}
            style={{
              border: 'none',
              background: 'transparent',
              color: '#ef4444',
              cursor: 'pointer',
              fontSize: 13
            }}
          >
            清除全部备注
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              disabled={saving}
              onClick={() => onClose?.()}
              style={{
                border: '1px solid #e2e8f0',
                background: '#fff',
                color: '#475569',
                borderRadius: 8,
                padding: '8px 14px',
                cursor: 'pointer',
                fontSize: 13
              }}
            >
              取消
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() => commit()}
              style={{
                border: 'none',
                background: '#2563eb',
                color: '#fff',
                borderRadius: 8,
                padding: '8px 14px',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600
              }}
            >
              {saving ? '保存中…' : '保存'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(panel, document.body);
}
